/**
 * FILE: src/blueprint/adapters/progressiveAdapter.js
 * GOAL:
 * - Normalize progressive server response into one stable client DTO.
 * - Keep server format interpretation in one place.
 * - Preserve canonical entry/zones structure for client rendering.
 * - Carry resolved assistant/coaching persona data forward into rail assistant entries
 *   so the UI can render avatar, name, slogan, and typing settings.
 */

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function asNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function normalizeAvatar(avatar) {
  const safeAvatar = asObject(avatar);

  return {
    src: asString(safeAvatar.src).trim(),
  };
}

function normalizeTyping(typing) {
  const safeTyping = asObject(typing);
  const speed = asObject(safeTyping.speed);

  return {
    speed: {
      min: asNumber(speed.min, 0),
      max: asNumber(speed.max, 0),
    },
    thinkChance: asNumber(safeTyping.thinkChance, 0),
    thinkExtraMin: asNumber(safeTyping.thinkExtraMin, 0),
    thinkExtraMax: asNumber(safeTyping.thinkExtraMax, 0),
    typoChance: asNumber(safeTyping.typoChance, 0),
  };
}

function normalizePersona(persona, assistantActive = {}) {
  const safePersona = asObject(persona);
  const safeAssistantActive = asObject(assistantActive);

  const personaAvatar = normalizeAvatar(safePersona.avatar);
  const fallbackAvatar = normalizeAvatar(safeAssistantActive.avatar);
  const personaTyping = normalizeTyping(safePersona.typing);
  const fallbackTyping = normalizeTyping(safeAssistantActive.typing);

  const avatarSrc = personaAvatar.src || fallbackAvatar.src;

  const hasPersonaTyping =
    personaTyping.speed.min > 0 ||
    personaTyping.speed.max > 0 ||
    personaTyping.thinkChance > 0 ||
    personaTyping.thinkExtraMin > 0 ||
    personaTyping.thinkExtraMax > 0 ||
    personaTyping.typoChance > 0;

  const typing = hasPersonaTyping ? personaTyping : fallbackTyping;

  return {
    name: asString(
      safePersona.name || safeAssistantActive.label || safeAssistantActive.title
    ).trim(),
    slogan: asString(safePersona.slogan).trim(),
    introduction: asString(safePersona.introduction).trim(),
    avatar: {
      src: avatarSrc,
    },
    typing,
    tone: asString(safePersona.tone).trim(),
    style: asString(safePersona.style).trim(),
    energy: asString(safePersona.energy).trim(),
  };
}

function normalizeBlock(block, entryId, blockIndex) {
  if (!block || typeof block !== 'object') return null;

  const type = asString(block.type).trim() || 'text';
  const title = asString(block.title).trim();
  const body =
    typeof block.body === 'string'
      ? block.body.trim()
      : typeof block.value === 'string'
        ? block.value.trim()
        : '';

  const format = asString(block.format).trim() || 'plain';
  const className = asString(block.className).trim();
  const image = asObject(block.image);

  if (type === 'choices') {
    const choices = asArray(block.choices)
      .map((choice) => {
        const safeChoice = asObject(choice);
        const id = asString(safeChoice.id).trim();
        const label = asString(safeChoice.label).trim();

        if (!id || !label) return null;

        return {
          ...safeChoice,
          id,
          label,
        };
      })
      .filter(Boolean);

    if (!choices.length) return null;

    return {
      ...block,
      id: asString(block.id).trim() || `${entryId}:block:${blockIndex}`,
      type,
      layout: asString(block.layout).trim() || 'stack',
      choices,
      title,
      className,
    };
  }

  if (type === 'icon') {
    const name = asString(block.name).trim();
    const label = asString(block.label).trim();

    if (!name) return null;

    return {
      ...block,
      id: asString(block.id).trim() || `${entryId}:block:${blockIndex}`,
      type,
      name,
      label,
      title,
      className,
    };
  }

  const hasImage = type === 'image' || (format === 'image' && asString(image.src).trim());

  if (!title && !body && !hasImage) return null;

  const normalized = {
    ...block,
    id: asString(block.id).trim() || `${entryId}:block:${blockIndex}`,
    type,
    title,
    body,
    text: body,
    format,
    className,
  };

  if (hasImage) {
    normalized.image = image;
  }

  return normalized;
}

function normalizePayload(payload) {
  return asObject(payload);
}

function normalizeEntry(entry, index, zoneName, assistantMeta = {}) {
  if (!entry || typeof entry !== 'object') return null;

  const entryId = asString(entry.id).trim() || `${zoneName || 'entry'}:${index}`;
  const sourceId = asString(entry.sourceId).trim();
  const kind = asString(entry.kind).trim() || 'info';
  const role = asString(entry.role).trim();
  const assistantId = asString(entry.assistantId).trim();
  const title = asString(entry.title).trim();
  const placement = asObject(entry.placement);
  const reportId = asString(entry.reportId).trim();
  const reportType = asString(entry.reportType).trim();
  const reportTitle = asString(entry.reportTitle).trim();
  const payload = normalizePayload(entry.payload);

  const rawBlocks = asArray(entry.blocks);
  const blocks = rawBlocks
    .map((block, blockIndex) => normalizeBlock(block, entryId, blockIndex))
    .filter(Boolean);

  const hasPayload = Object.keys(payload).length > 0;

  if (!blocks.length && !hasPayload) {
    return null;
  }

  const normalized = {
    id: entryId,
    sourceId,
    kind,
    role,
    assistantId,
    title,
    reportId,
    reportType,
    reportTitle,
    zone: zoneName || asString(placement.zone).trim() || '',
    placement,
    blocks,
    payload,
    raw: entry,
  };

  if ((role === 'assistant' || role === 'coach') && assistantMeta.active) {
    normalized.persona = assistantMeta.persona;
  }

  return normalized;
}

function normalizeEntries(entries, zoneName, assistantMeta = {}) {
  return asArray(entries)
    .map((entry, index) => normalizeEntry(entry, index, zoneName, assistantMeta))
    .filter(Boolean);
}

function normalizeProgressPopover(progressPopover) {
  const safePopover = asObject(progressPopover);

  return {
    enabled: Boolean(safePopover.enabled),
    elements: asArray(safePopover.elements)
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const hasLabel = Object.prototype.hasOwnProperty.call(item, 'label');
        const value = asString(item.value).trim();
        const label = hasLabel ? asString(item.label) : '';
        const type = asString(item.type).trim() || 'text';
        const className = asString(item.className).trim();

        if (!value) {
          return null;
        }

        const normalized = {
          value,
          type,
        };

        if (hasLabel) {
          normalized.label = label;
        }

        if (className) {
          normalized.className = className;
        }

        return normalized;
      })
      .filter(Boolean),
  };
}

function normalizePilotIdentity(identity) {
  const safeIdentity = asObject(identity);

  return {
    wpUserId: asNumber(safeIdentity.wp_user_id, 0),
    email: asString(safeIdentity.email).trim(),
    displayName: asString(safeIdentity.display_name).trim(),
    pilotProfileType: asString(safeIdentity.pilot_profile_type).trim(),
    runId: asNumber(safeIdentity.run_id, 0),
    generatedAt: asString(safeIdentity.generated_at).trim(),
  };
}

function extractMeta(data, fallbackRequestId = null) {
  const progress = asObject(data?.progress);
  const confidence = asObject(data?.confidence);
  const assistant = asObject(data?.assistant);
  const assistantActive = asObject(assistant?.active);
  const diagnostics = asObject(data?.diagnostics);
  const render = asObject(diagnostics?.render);
  const results = asObject(diagnostics?.results);
  const resultsMeta = asObject(results?.meta);
  const progressPopover = normalizeProgressPopover(data?.progressPopover);

  const persona = normalizePersona(assistantActive.persona, assistantActive);
  const pilotIdentity = normalizePilotIdentity(data?.pilot_identity);

  return {
    progress: {
      answered: asNumber(progress?.answered, 0),
      total: asNumber(progress?.total, 0),
      ratio: asNumber(progress?.ratio, 0),
    },
    confidence: {
      level: asString(confidence?.level),
    },
    assistant: {
      requested:
        assistant && Object.prototype.hasOwnProperty.call(assistant, 'requested')
          ? assistant.requested
          : null,
      active: assistantActive,
      persona,
    },
    pilotIdentity,
    progressPopover,
    render: {
      ok: Boolean(render?.ok),
      schemaVersion: asString(render?.schemaVersion || '2.0.0'),
      pack_id: asString(render?.pack_id),
      module_id: asString(render?.module_id),
      track_id: asString(render?.track_id),
      title: asString(render?.title),
      progress_ratio: asNumber(render?.progress_ratio, 0),
    },
    results,
    resultsMeta,
    request_id:
      asString(resultsMeta?.request_id) ||
      asString(data?.request_id) ||
      asString(fallbackRequestId) ||
      null,
  };
}

export function progressiveAdapter(response) {
  const envelope = asObject(response);
  const data = asObject(envelope?.data ?? envelope);

  const meta = extractMeta(data, envelope?.meta?.request_id ?? null);

  const assistantMeta = {
    active: meta.assistant.active,
    persona: meta.assistant.persona,
  };

  const zones = asObject(data?.zones);

  const railEntries = normalizeEntries(
    asArray(zones?.rail).length ? zones.rail : data?.rail,
    'rail',
    assistantMeta
  );

  const stepEntries = normalizeEntries(
    asArray(zones?.steps).length ? zones.steps : data?.steps,
    'steps',
    assistantMeta
  );

  const allEntries = normalizeEntries(
    asArray(data?.entries).length
      ? data.entries
      : [...railEntries.map((entry) => entry.raw), ...stepEntries.map((entry) => entry.raw)],
    '',
    assistantMeta
  );

  const railInsights = railEntries.filter((entry) => entry.role === 'insight');
  const railAssistants = railEntries.filter(
    (entry) => entry.role === 'assistant' || entry.role === 'coach'
  );
  const railReports = railEntries.filter((entry) => entry.role === 'report');

  return {
    moduleId: asString(data?.module_id),
    trackId: asString(data?.track),
    module: asObject(data?.module),
    insightsState: asString(data?.insights_state),
    resolved: asObject(data?.resolved),
    progress: meta.progress,
    confidence: meta.confidence,
    progressPopover: meta.progressPopover,
    entries: allEntries,
    zones: {
      rail: railEntries,
      steps: stepEntries,
    },
    railEntries,
    stepEntries,
    railInsights,
    railAssistants,
    railReports,
    assistant: meta.assistant,
    diagnostics: asObject(data?.diagnostics),
    meta,
    updatedAt: data?.updated_at ?? null,
    raw: data,
  };
}

export default progressiveAdapter;
